/**
 * O que faz: hooks React Query da organização do Kanban (configuração da clínica, publicação e painel admin).
 * Onde usa: SecaoAgentes, ConfiguracaoOrganizacaoKanban e LimitesOrganizacaoAdmin.
 * Conexões: servico.ts da organização; toasts em português; sem polling nem Realtime.
 * Tabelas: indiretamente as das RPCs de servico.ts, sempre filtradas pela clínica no servidor.
 * Data: 21/10/2026.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { CortesOrganizacao, RascunhoOrganizacao } from './contratos';
import * as servico from './servico';

export const chavesOrganizacao = {
  configuracao: (clinicaId: string | null) => ['agentes', 'organizacao', clinicaId] as const,
  resumoAdmin: (clinicaId: string | null) => ['agentes', 'organizacao-admin', clinicaId] as const,
};

const mensagemDe = (falha: unknown, padrao: string) => (falha instanceof Error && falha.message ? falha.message : padrao);

export function useOrganizacaoKanban(clinicaId: string | null) {
  const queryClient = useQueryClient();
  const consulta = useQuery({
    queryKey: chavesOrganizacao.configuracao(clinicaId),
    queryFn: () => servico.carregarOrganizacao(clinicaId as string),
    enabled: Boolean(clinicaId),
    // Configuração muda pouco; sem refetchInterval para não competir com o rascunho em edição.
    staleTime: 60_000,
  });
  const publicar = useMutation({
    mutationFn: (rascunho: RascunhoOrganizacao) => {
      if (!clinicaId) throw new Error('Clínica ativa não encontrada.');
      return servico.publicarOrganizacao(clinicaId, rascunho);
    },
    onSuccess: () => {
      // Colunas sugeridas podem ter sido criadas: Pipeline, etapas e revisão atualizam juntos.
      void queryClient.invalidateQueries({ queryKey: ['agentes'] });
      void queryClient.invalidateQueries({ queryKey: ['etapas'] });
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.success('Organização salva. Você pode revisar as sugestões antes de mover.');
    },
    // O erro fica no assistente (mensagem específica e rascunho preservado); sem toast duplicado.
  });
  return { consulta, publicar };
}

export function useResumoOrganizacaoAdmin(clinicaId: string | null, habilitado: boolean) {
  return useQuery({
    queryKey: chavesOrganizacao.resumoAdmin(clinicaId),
    queryFn: () => servico.carregarResumoAdmin(clinicaId as string),
    enabled: Boolean(clinicaId) && habilitado,
    staleTime: 60_000,
  });
}

export function useAcoesOrganizacaoAdmin(clinicaId: string | null) {
  const queryClient = useQueryClient();
  const comClinica = () => {
    if (!clinicaId) throw new Error('Clínica ativa não encontrada.');
    return clinicaId;
  };
  // Painel técnico e a leitura da clínica (rotas calibradas) mudam juntos depois de cada ação.
  const atualizar = () => void queryClient.invalidateQueries({ queryKey: ['agentes'] });
  const erro = (padrao: string) => (falha: unknown) => toast.error(mensagemDe(falha, padrao));
  return {
    definirLimites: useMutation({
      mutationFn: (limites: { jev: number; llm: number; usd: number }) => servico.definirLimites(comClinica(), limites),
      onSuccess: () => { atualizar(); toast.success('Limites diários salvos.'); },
      onError: erro('Não foi possível salvar os limites.'),
    }),
    definirCortes: useMutation({
      mutationFn: (cortes: CortesOrganizacao) => servico.definirCortes(comClinica(), cortes),
      onSuccess: () => { atualizar(); toast.success('Cortes salvos. Liberações automáticas anteriores foram revogadas.'); },
      onError: erro('Não foi possível salvar os cortes técnicos.'),
    }),
    registrarPrivacidade: useMutation({
      mutationFn: ({ validada, referencia }: { validada: boolean; referencia: string }) => servico.registrarPrivacidade(comClinica(), validada, referencia),
      onSuccess: (_resultado, { validada }) => { atualizar(); toast.success(validada ? 'Privacidade validada para os modelos atuais.' : 'Validação de privacidade removida.'); },
      onError: erro('Não foi possível registrar a privacidade.'),
    }),
    aprovarRegra: useMutation({
      mutationFn: ({ etapaId, versao }: { etapaId: string; versao: string }) => servico.aprovarRegra(comClinica(), etapaId, versao),
      onSuccess: () => { atualizar(); toast.success('Coluna liberada para movimento automático. A clínica ainda escolhe se quer usar.'); },
      onError: erro('Não foi possível aprovar esta coluna.'),
    }),
    desativar: useMutation({
      mutationFn: () => servico.desativarOrganizacao(comClinica()),
      onSuccess: () => {
        atualizar();
        // Selos do Pipeline deixam de mostrar a coluna sugerida pelo motor desligado.
        void queryClient.invalidateQueries({ queryKey: ['lead-classification-states'] });
        toast.success('Clínica de volta ao motor legado. Regras e histórico continuam guardados.');
      },
      onError: erro('Não foi possível voltar esta clínica ao motor legado.'),
    }),
    revogarRegra: useMutation({
      mutationFn: (etapaId: string) => servico.revogarRegra(comClinica(), etapaId),
      onSuccess: () => { atualizar(); toast.success('Liberação revogada. A coluna voltou a só sugerir.'); },
      onError: erro('Não foi possível revogar esta liberação.'),
    }),
  };
}
